import { TTrafficFilterName } from 'src/entities/traffic-filters/model/types';

import axios from '../utils/axios';
import { isResponseSuccess } from '../utils/api';

const CAMPAIGN_STATUS = {
  BANNED: 4,
} as const;

type TApiResponse<T> = {
  status?: number;
  statusText?: string;
  total?: number;
  data?: T & { error?: string };
};

type TDictionary = {
  items: string[];
};

function API(tokenData = {}, showErrorNoty = true) {
  const API_HOST = '/api';

  const urlString = (params) => {
    if (!params) return '';
    const urlParams = [];
    for (const [name, value] of Object.entries(params)) {
      if (name === 'email') {
        urlParams.push(`${encodeURIComponent(name)}=${encodeURIComponent(value).replace(/%40/g, '@')}`);
      } else {
        urlParams.push(`${name}=${encodeURIComponent(value)}`);
      }
    }
    return urlParams.join('&');
  };

  const makeUrl = (
    uri = '',
    options = {},
    useApi = true,
  ) => (useApi
    ? `${API_HOST}/${uri}?${urlString(options)}`
    : `${uri}?${urlString(options)}`);

  /**
   * @param url {string}
   * @param directUrl {boolean}
   * @param urlParams {object}
   * @param headers {object}
   * @returns {Promise<any>}
   * @constructor
   */
  const GET = (
    url = '',
    directUrl = false,
    urlParams = {},
    headers = {},
    useApi = true,
    blob = false,
  ) => axios({
    method: 'GET',
    url: directUrl ? url : makeUrl(url, urlParams, useApi),
    headers: {
      Cache: 'no-cache',
      ...(tokenData?.authorization ? { Authorization: tokenData?.authorization } : {}),
      ...headers,
    },
    credentials: 'same-origin',
    showErrorNoty,
    ...(blob ? { responseType: 'blob' } : {}),
  }).then((res) => {
    if (blob) {
      return res;
    }
    if (isResponseSuccess(res.status)) {
      return {
        status: res.status,
        data: res.data,
        total: res.headers['x-total-count'] || 0,
        headers: res.headers,
        resTotal: res.total,
      };
    }
    return {};
  }).catch((error) => {
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx\
      const errRes = {
        data: error.response.data,
        status: error.response.status,
        statusText: error.response.statusText,
      };
      console.error('GET ERROR RESPONSE', errRes, showErrorNoty, url);
      return errRes;
    }
    return {};
  });

  /**
   * @param url {string}
   * @param data {object}
   * @param directUrl {boolean}
   * @param useApi {boolean}
   * @param urlParams {object}
   * @param headers {object}
   * @param credentials {string}
   * @returns {Promise<any>}
   * @constructor
   */
  const POST = (
    url = '',
    data = {},
    directUrl = false,
    useApi = true,
    urlParams = {},
    headers = {},
    credentials = 'same-origin',
  ) => axios({
    method: 'POST',
    data,
    body: JSON.stringify(data),
    url: directUrl ? url : makeUrl(url, urlParams, useApi),
    headers: {
      Cache: 'no-cache',
      ...(tokenData?.authorization ? { Authorization: tokenData?.authorization } : {}),
      ...headers,
    },
    credentials,
    showErrorNoty,
  }).then((res) => {
    if (res.status === 200 || res.status === 201) {
      return {
        status: res.status,
        data: res.data,
        total: res.headers['x-total-count'] || 0,
        headers: res.headers,
      };
    }
    return {
      status: res.status,
    };
  }).catch((error) => {
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx\

      const errRes = {
        data: error.response.data,
        status: error.response.status,
        statusText: error.response.statusText,
      };
      console.error('POST ERROR RESPONSE', errRes);
      return errRes;
    }
    return {};
  });

  /**
   * @param url {string}
   * @param directUrl {boolean}
   * @param urlParams {object}
   * @returns {Promise<any>}
   * @constructor
   */
  const PUT = (
    url = '',
    data = {},
    directUrl = false,
    useApi = true,
    urlParams = {},
  ) => axios({
    method: 'PUT',
    url: directUrl ? url : makeUrl(url, urlParams, useApi),
    data,
    body: JSON.stringify(data),
    headers: {
      Cache: 'no-cache',
      ...(tokenData?.authorization ? { Authorization: tokenData?.authorization } : {}),
    },
    credentials: 'same-origin',
    showErrorNoty,
  }).then((res) => {
    if (res.status === 200 || res.status === 201 || res.status === 204) {
      return {
        status: res.status,
        data: res.data,
        total: res.headers['x-total-count'] || 0,
        headers: res.headers,
      };
    }
    return {
      status: res.status,
    };
  }).catch((error) => {
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx\
      const errRes = {
        data: error.response.data,
        status: error.response.status,
        statusText: error.response.statusText,
      };
      console.error('PUT ERROR RESPONSE', errRes);
      return errRes;
    }
    return {};
  });

  /**
   * @param url {string}
   * @param data {object}
   * @param directUrl {boolean}
   * @param urlParams {object}
   * @returns {Promise<any>}
   * @constructor
   */
  const PATCH = (
    url = '',
    data = {},
    directUrl = false,
    useApi = true,
    urlParams = {},
  ) => axios({
    method: 'PATCH',
    url: directUrl ? url : makeUrl(url, urlParams, useApi),
    data,
    body: JSON.stringify(data),
    headers: {
      Cache: 'no-cache',
      ...(tokenData?.authorization ? { Authorization: tokenData?.authorization } : {}),
    },
    credentials: 'same-origin',
    showErrorNoty,
  }).then((res) => {
    if (res.status === 200) {
      return {
        status: res.status,
        data: res.data,
        total: res.headers['x-total-count'] || 0,
        headers: res.headers,
      };
    }
    return {
      status: res.status,
    };
  }).catch((error) => {
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx\
      const errRes = {
        data: error.response.data,
        status: error.response.status,
        statusText: error.response.statusText,
      };
      console.error('PATCH ERROR RESPONSE', errRes);
      return errRes;
    }
    return {};
  });

  /**
   * @param url {string}
   * @param directUrl {boolean}
   * @param urlParams {object}
   * @returns {Promise<any>}
   * @constructor
   */
  const DELETE = (url = '', data = {}, directUrl = false, urlParams = {}) => axios({
    method: 'DELETE',
    url: directUrl ? url : makeUrl(url, urlParams),
    body: JSON.stringify(data),
    headers: {
      Cache: 'no-cache',
      ...(tokenData?.authorization ? { Authorization: tokenData?.authorization } : {}),
    },
    credentials: 'same-origin',
    showErrorNoty,
  }).then((res) => {
    if (res.status === 200 || res.status === 204) {
      return {
        status: res.status,
        data: res.data,
        total: res.headers['x-total-count'] || 0,
        headers: res.headers,
      };
    }
    return {};
  }).catch((error) => {
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx\
      const errRes = {
        data: error.response.data,
        status: error.response.status,
        statusText: error.response.statusText,
      };
      console.error('DELETE ERROR RESPONSE', errRes);
      return errRes;
    }
    return {};
  });

  return {
    BASIC: {
      ENV: {
        // Absolute path: a relative 'env' resolves against the current route
        // (e.g. /users/edit/env) and the SPA catch-all answers it with index.html.
        load: () => GET('/env', true),
      },
      CURRENCIES: {
        load: () => GET('currencies'),
      },
      PLANS: {
        load: () => GET('admin/plans'),
      },
      BUNDLES: {
        load: () => GET('billing/bundles'),
      },
    },
    USER: {
      BASIC: {
        load: (headers = {}) => GET(
          'auth',
          false,
          {},
          headers,
        ),
        update: (data = {}) => PUT('me', { ...data }),
      },
      AUTH: {
        login: (urlParams) => POST(
          'auth/login/admin',
          urlParams,
          false,
        ),
        refreshToken: (urlParams = {}, headers = {}) => GET(
          'auth/refresh',
          false,
          urlParams,
          headers,
        ),
        logOut: (urlParams) => DELETE('auth/sessions', false, null, urlParams),
      },
    },
    USERS: {
      load: (urlParams = {}) => GET(
        'admin/users',
        false,
        urlParams,
      ),
      update: (data = {}) => PUT(`admin/users/${data.id}`, { ...data }),
      loadById: (id = '') => GET(
        `admin/users/${id}`,
      ),
      loadBundlesV2: (id = '') => GET(
        `admin/users/${id}/bundles_v2`,
      ),
      delete: (id = '') => DELETE(
        `admin/users/${id}`,
      ),
      become: (id = '') => POST(
        `admin/users/${id}/become`,
      ),
      export: (urlParams = {}) => GET(
        'admin/users/export',
        false,
        urlParams,
      ),

      getSources: (id = '', urlParams = {}) => GET(
        `admin/users/${id}/sources`,
        false,
        urlParams,
      ),
      updateSources: (data = {
        user_id: '',
        source_id: '',
        currency: '',
      }) => PATCH(`admin/users/${data.user_id}/sources/${data.source_id}`, {
        currency: data.currency,
      }),

      getNetworks: (id = '', urlParams = {}) => GET(
        `admin/users/${id}/networks`,
        false,
        urlParams,
      ),
      updateNetworks: (data = {
        user_id: '',
        network_id: '',
        currency: '',
      }) => PATCH(`admin/users/${data.user_id}/networks/${data.network_id}`, {
        currency: data.currency,
      }),
      updateParallelTracking: (data) => PATCH(
        `admin/users/${data.id}/campaigns/${data.campaign_id}`,
        data,
      ),
    },
    DASHBOARD: {
      clicks: (urlParams) => GET(
        'admin/stat/basic',
        false,
        urlParams,
      ),
    },
    TRANSACTIONS: {
      load: (urlParams = {}) => GET(
        'admin/transactions',
        false,
        urlParams,
      ),
      REPORT: {
        load: (urlParams = {}) => GET(
          'admin/stat/transactions',
          false,
          urlParams,
        ),
        exportReport: (urlParams = {}) => GET(
          'admin/transactions/export/csv',
          false,
          urlParams,
        ),
        exportInvoice: (urlParams = {}) => GET(
          'admin/transactions/export/pdf',
          false,
          urlParams,
        ),
      },
      INVOICE: {
        load: (id = '') => GET(
          `admin/transactions/invoice/${id || ''}`,
          false,
          {},
          {},
          true,
          true,
        ),
        DETAILS: {
          load: () => GET(
            'admin/invoice_details',
          ),
          create: (data = {}) => POST(
            'admin/invoice_details',
            data,
          ),
        },
        MANUAL: {
          create: (data = {}) => POST(
            'admin/transactions/manual_invoice',
            data,
          ),
          update: (id, data = {}) => PUT(
            `admin/transactions/manual_invoice/${id}`,
            data,
          ),
          previewItem: (data = {}) => POST(
            'admin/transactions/manual_invoice/preview_item',
            data,
          ),
        },
      },
      MANUAL_INVOICE: {
        update: (id = '', data = {}) => PATCH(
          `admin/transactions/manual_invoice/${id}`,
          data,
        ),
      },
    },
    SPECIAL_OFFERS: {
      load: (urlParams = {}) => GET(
        'admin/special_offers',
        false,
        urlParams,
      ),
      create: (data = {}) => POST(
        'admin/special_offers',
        data,
      ),
      update: (data = {}) => {
        const { id, ...restData } = data;
        return PATCH(`admin/special_offers/${id}`, restData);
      },
    },
    DOMAINS: {
      load: (urlParams = {}) => GET(
        'admin/domains',
        false,
        urlParams,
      ),
    },
    REFERRALS: {
      load: (urlParams = {}) => GET(
        'admin/referral',
        false,
        urlParams,
      ),
      payout: (data = {}) => POST(
        'admin/referral/payouts',
        data,
        false,
      ),
      attach: (data = {}) => POST(
        'admin/attach_referral',
        data,
        false,
      ),
      createConversion: (data = {}) => POST(
        'admin/referral/conversion',
        data,
        false,
      ),
    },
    SOURCE_PRESETS: {
      /**
       * @param urlParams
       * @param urlParams.page {number}
       * @param urlParams.per {number}
       * @returns {Promise<*>}
       */
      load: (urlParams = {
        page: 1,
        per: 100,
      }) => GET(
        'admin/source_presets',
        false,
        urlParams,
      ),
      /**
       * @param id
       * @returns {Promise<*>}
       */
      loadById: (id = '') => GET(
        `admin/source_presets/${id}`,
      ),
      /**
       * @param payloadData {object}
       * @returns {Promise<*>}
       */
      create: (payloadData = {}) => POST(
        'admin/source_presets',
        payloadData,
        false,
      ),
      /**
       * @param payloadData {object}
       * @returns {Promise<any>}
       */
      update: (payloadData = {}) => PUT(
        `admin/source_presets/${payloadData.id}`,
        payloadData,
      ),
      /**
       * @param id
       * @returns {Promise<*>}
       */
      delete: (id) => DELETE(
        `admin/source_presets/${id}`,
      ),
    },
    PRESET_CATEGORIES: {
      load: (urlParams = {}) => GET(
        'admin/catalog_presets',
        false,
        urlParams,
      ),
      loadById: (id = '') => GET(
        `admin/catalog_presets/${id}`,
      ),
      create: (payloadData = {}) => POST(
        'admin/catalog_presets',
        payloadData,
        false,
      ),
      update: (payloadData = {}) => PUT(
        `admin/catalog_presets/${payloadData.id}`,
        payloadData,
      ),
      delete: (id) => DELETE(
        `admin/catalog_presets/${id}`,
      ),
      reorder: (type, idsArray = []) => POST(
        `admin/catalog_presets/reorder/${type}`,
        idsArray,
        false,
      ),
    },
    NETWORK_PRESETS: {
      /**
       * @param urlParams
       * @param urlParams.page {number}
       * @param urlParams.per {number}
       * @returns {Promise<*>}
       */
      load: (urlParams = {}) => GET(
        'admin/network_presets',
        false,
        urlParams,
      ),
      /**
       * @param id
       * @returns {Promise<*>}
       */
      loadById: (id = '') => GET(
        `admin/network_presets/${id}`,
      ),
      /**
       * @param payloadData
       * @param payloadData.alias {string}
       * @param payloadData.title {string}
       * @param payloadData.url {string}
       * @param payloadData.logo {string}
       * @param payloadData.categories {array}
       * @param payloadData.postback_url {string}
       * @param payloadData.clickid {string}
       * @param payloadData.sum {string}
       * @param payloadData.offer_url {string}
       * @param payloadData.contact {object}
       * @param payloadData.contact.person {string}
       * @param payloadData.contact.email {string}
       * @param payloadData.contact.phone {string}
       * @param payloadData.contact.messenger {string}
       * @param payloadData.referral_link {string}
       * @param payloadData.comment {string}
       * @returns {Promise<*>}
       */
      create: (payloadData = {
        alias: '',
        title: '',
        url: '',
        logo: '',
        categories: [],
        postback_url: '',
        clickid: '',
        sum: '',
        offer_url: '',
        contact: {
          person: '',
          email: '',
          phone: '',
          messenger: '',
        },
        referral_link: '',
        comment: '',
      }) => POST(
        'admin/network_presets',
        payloadData,
        false,
      ),
      /**
       * @param payloadData
       * @param payloadData.id {string} required
       * @param payloadData.alias {string}
       * @param payloadData.title {string}
       * @param payloadData.url {string}
       * @param payloadData.logo {string}
       * @param payloadData.categories {array}
       * @param payloadData.postback_url {string}
       * @param payloadData.clickid {string}
       * @param payloadData.sum {string}
       * @param payloadData.offer_url {string}
       * @param payloadData.contact {object}
       * @param payloadData.contact.person {string}
       * @param payloadData.contact.email {string}
       * @param payloadData.contact.phone {string}
       * @param payloadData.contact.messenger {string}
       * @param payloadData.referral_link {string}
       * @param payloadData.comment {string}
       * @returns {Promise<*>}
       */
      update: (payloadData = {
        id: '',
        alias: '',
        title: '',
        url: '',
        logo: '',
        categories: [],
        postback_url: '',
        clickid: '',
        sum: '',
        offer_url: '',
        contact: {
          person: '',
          email: '',
          phone: '',
          messenger: '',
        },
        referral_link: '',
        comment: '',
      }) => PUT(
        `admin/network_presets/${payloadData.id}`,
        payloadData,
      ),
      /**
       * @param id
       * @returns {Promise<*>}
       */
      delete: (id) => DELETE(
        `admin/network_presets/${id}`,
      ),
    },
    PROMO_CODES: {
      /**
       * @param urlParams
       * @param urlParams.page {number}
       * @param urlParams.per {number}
       * @returns {Promise<*>}
       */
      load: (urlParams = {
        page: 1,
        per: 100,
      }) => GET(
        'admin/promo_codes',
        false,
        urlParams,
      ),
      /**
       * @param id
       * @returns {Promise<*>}
       */
      loadById: (id = '') => GET(
        `admin/promo_codes/${id}`,
      ),
      /**
       * @param payloadData
       * @param payloadData.title {string}
       * @param payloadData.discount {object}
       * @param payloadData.discount.kind {number}
       * @param payloadData.discount.quantity {number}
       * @param payloadData.discount.value {number}
       * @param payloadData.status {number}
       * @param payloadData.description {string}
       * @returns {Promise<*>}
       */
      create: (payloadData = {
        id: '',
        title: '',
        discount: {
          kind: 0,
          quantity: 0,
          value: 0,
        },
        status: 0,
        description: '',
      }) => POST(
        'admin/promo_codes',
        payloadData,
        false,
      ),
      /**
       * @param payloadData {object}
       * @param payloadData.id {string} required
       * @param payloadData.title {string}
       * @param payloadData.discount {object}
       * @param payloadData.discount.kind {number}
       * @param payloadData.discount.quantity {number}
       * @param payloadData.discount.value {number}
       * @param payloadData.status {number}
       * @param payloadData.description {string}
       * @returns {Promise<any>}
       */
      update: (payloadData = {
        id: '',
        title: '',
        discount: {
          kind: 0,
          quantity: 0,
          value: 0,
        },
        status: 0,
        description: '',
      }) => PUT(
        `admin/promo_codes/${payloadData.id}`,
        payloadData,
      ),
      /**
       * @param id
       * @returns {Promise<*>}
       */
      delete: (id) => DELETE(
        `admin/promo_codes/${id}`,
      ),
    },
    NEWS: {
      /**
       * @param urlParams
       * @param urlParams.page {number}
       * @param urlParams.per {number}
       * @returns {Promise<*>}
       */
      load: (urlParams = {
        page: 1,
        per: 100,
      }) => GET(
        'admin/news',
        false,
        urlParams,
      ),
      /**
       * @param id
       * @returns {Promise<*>}
       */
      loadById: (id = '') => GET(
        `admin/news/${id}`,
      ),
      /**
       * @param payloadData {object}
       * @param payloadData.title {string}
       * @param payloadData.content {string}
       * @param payloadData.date {string}
       * @returns {Promise<*>}
       */
      create: (payloadData = {
        title: '',
        content: '',
        date: '',
      }) => POST(
        'admin/news',
        payloadData,
        false,
      ),
      /**
       * @param payloadData {object}
       * @param payloadData.id {string} required
       * @param payloadData.title {string}
       * @param payloadData.content {string}
       * @param payloadData.date {string}
       * @returns {Promise<any>}
       */
      update: (payloadData = {
        id: '',
        title: '',
        content: '',
        date: '',
      }) => PUT(
        `admin/news/${payloadData.id}`,
        payloadData,
      ),
      /**
       * @param id
       * @returns {Promise<*>}
       */
      delete: (id) => DELETE(
        `admin/news/${id}`,
      ),
    },
    CAMPAIGNS: {
      load: (urlParams = {}) => GET(
        'admin/campaigns',
        false,
        urlParams,
      ),
      ban: ({ userId, campaignId }) => PATCH(
        `admin/users/${userId}/campaigns/${campaignId}`,
        { status: CAMPAIGN_STATUS.BANNED },
      ),
    },
    TRAFFIC_FILTERS: {
      load: (name: TTrafficFilterName): Promise<TApiResponse<TDictionary>> => GET(
        `admin/dictionaries/${name}`,
      ),
      update: (
        name: TTrafficFilterName,
        items: string[] = [],
      ): Promise<TApiResponse<TDictionary>> => PUT(
        `admin/dictionaries/${name}`,
        { items },
      ),
    },
  };
}

export default API;
